import { NetworkStatus } from '@apollo/client';
import {
  ErrorBoundary,
  ErrorRetry,
  FooterButtonStyled,
  MediaUpload,
  SectionHeader,
  SuspenseLoader,
  useProjectMilestoneTypeReasonsQuery,
} from '@bechtel/client-common';
import {
  Box,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Spinner,
  SpinnerSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  AttachmentMetadataInput,
  CommodityStatusCode,
  ProjectMilestoneTypeRejectionReason,
  RejectFitupInput,
  RejectFitupReason,
  RejectFitupRequestInput,
  RejectWeldInput,
  RejectWeldSubReason,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { Formik } from 'formik';
import { find, get, map, some } from 'lodash';
import { FC, Fragment, Suspense, useEffect, useState } from 'react';
import { array, object, string } from 'yup';
import { Reason, ReasonList, SubReason, SubReasonList } from '~/components/molecules';
import { useRejectWeld } from '~/hooks';
import { useRejectFitup } from '~/hooks/use-reject-fitup';
import { useRejectFitupRequest } from '~/hooks/use-reject-fitup-request';
import { RejectWeldErrorModal } from './error';
import { RejectWeldErrorExt } from './error/types';
import { WeldSendBackProps } from './types';

interface FormikState {
  reasonType: Reason | null;
  reasonComment?: string;
  mediaMetadata?: AttachmentMetadataInput[];
  subReasonSelectedList: SubReason[];
  subReasonList: [];
  subReasonComment?: string;
}

const initialValues: FormikState = {
  reasonType: null,
  reasonComment: '',
  mediaMetadata: [],
  subReasonList: [],
  subReasonSelectedList: [],
  subReasonComment: '',
};
const weldValidationSchema = object().shape({
  reasonType: object().required(t`Select Reason`),
  subReasonSelectedList: array().when('subReasonList', {
    is: (list) => Array.isArray(list) && list.length > 0,
    then: array()
      .required()
      .min(1, t`Select at least one sub reason.`),
    otherwise: array(),
  }),
  reasonComment: string().when('reasonType.label', {
    is: (value) => value == RejectFitupReason.OTHER_REASON,
    then: string()
      .required(t`Comment is mandatory`)
      .min(2, t`Too Short!`)
      .max(2000, t`Too Long!`),
    otherwise: string(),
  }),
  subReasonComment: string().when('subReasonSelectedList', {
    is: (value) => some(value, (reason) => reason.label == RejectWeldSubReason.OTHER_SUB_REASON),
    then: string()
      .required(t`Comment is mandatory`)
      .min(2, t`Too Short!`)
      .max(2000, t`Too Long!`),
    otherwise: string(),
  }),
});

export const WeldSendBack: FC<WeldSendBackProps> = ({
  currentMilestone,
  fitUpMilestone,
  commodityStatus,
  fitupProjectMilestoneDetails,
  isSendBackToForeman,
  onClose,
  refetchQueries,
}) => {
  const [getProjectMilestoneTypeRejectionReasons, { data, loading, error, refetch, networkStatus }] =
    useProjectMilestoneTypeReasonsQuery();

  const fitupMilestoneTypeId = fitUpMilestone ? fitUpMilestone.type.id : fitupProjectMilestoneDetails?.id;

  const projectMilestoneTypeRejectionReasons: ProjectMilestoneTypeRejectionReason[] = get(
    data,
    'projectMilestoneTypeRejectionReasons',
    []
  );

  const sendBackReasonList: Reason[] = map(projectMilestoneTypeRejectionReasons, ({ id, reason }) => ({
    label: reason,
    value: id,
  }));

  useEffect(() => {
    if (!currentMilestone || !currentMilestone.type || !currentMilestone.type.id) return;

    if (!isSendBackToForeman) {
      getProjectMilestoneTypeRejectionReasons({
        variables: {
          input: {
            projectMilestoneTypeId: fitupMilestoneTypeId,
          },
        },
      });
    } else {
      getProjectMilestoneTypeRejectionReasons({
        variables: { input: { projectMilestoneTypeId: currentMilestone.type.id } },
      });
    }
  }, [
    currentMilestone,
    getProjectMilestoneTypeRejectionReasons,
    fitUpMilestone,
    fitupMilestoneTypeId,
    commodityStatus,
    isSendBackToForeman,
  ]);

  const [rejectWeldError, setRejectWeldError] = useState<RejectWeldErrorExt | null>();
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const dispatchToast = useToastsDispatch();

  const closeModal = () => {
    setRejectWeldError(null);
  };
  const [rejectWeld, { loading: mutationLoading, error: mutationError, data: mutationData }] = useRejectWeld(() => {
    setActionCompleted(true);
  }, refetchQueries);
  const [rejectFitup, { loading: rejectFitupLoading, error: rejectFitupError, data: rejectFitupData }] = useRejectFitup(
    () => {
      setActionCompleted(true);
    },
    refetchQueries
  );

  const [
    rejectFitupRequest,
    { loading: rejectFitupRequestLoading, error: rejectFitupRequestError, data: rejectFitupRequestData },
  ] = useRejectFitupRequest(() => {
    setActionCompleted(true);
  }, refetchQueries);

  useEffect(() => {
    if (!actionCompleted) return;
    const error =
      get(mutationData, 'rejectWeld.error') ||
      get(rejectFitupData, 'rejectFitup.error') ||
      get(rejectFitupRequestData, 'rejectFitupRequest.error') ||
      mutationError ||
      rejectFitupError ||
      rejectFitupRequestError;
    if (!error) {
      const weld =
        get(mutationData, 'rejectWeld.weld') ||
        get(rejectFitupData, 'rejectFitup.weld') ||
        get(rejectFitupRequestData, 'rejectFitupRequest.weld');
      const createRejectWeldToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${t`Weld sent back to`} ${
            weld.status?.status === CommodityStatusCode.SENT_BACK_TO_PIPING ? t`piping` : t`foreman`
          } ${t`successfully.`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(createRejectWeldToast);
      onClose && onClose();
    } else {
      setRejectWeldError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted]);

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          p={{ sm: '500' }}
          style={{ height: '100%' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box
          data-testid="retry"
          alignItems={{ sm: 'center' }}
          p={{ sm: '500' }}
          justifyContent={{ sm: 'center' }}
          style={{ height: '100%' }}
        >
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading rejection Reasons. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}

      {!loading && !error && (
        <Formik
          initialValues={initialValues}
          validationSchema={weldValidationSchema}
          onSubmit={async (values) => {
            const rejectWeldInput: RejectWeldInput | RejectFitupInput = {
              commodityMilestoneId:
                fitUpMilestone?.id && !isSendBackToForeman ? fitUpMilestone?.id : currentMilestone?.id,
              rejectionReasonTypeId: get(values, 'reasonType.value'),
              otherReasonComments: encodeURIComponent(get(values, 'reasonComment', '')),
              rejectionSubReasonTypeIds: map(values?.subReasonSelectedList, 'value'),
              otherSubReasonComments: encodeURIComponent(values?.subReasonComment ? values?.subReasonComment : ''),
              attachmentMetadata: values.mediaMetadata,
            };

            if (!isSendBackToForeman) {
              if (fitUpMilestone?.id) {
                rejectFitup({
                  variables: {
                    input: rejectWeldInput,
                  },
                });
              } else {
                const rejectFitupRequestInput: RejectFitupRequestInput = {
                  ...rejectWeldInput,
                  projectMilestoneTypeId: fitupProjectMilestoneDetails?.id,
                };

                rejectFitupRequest({
                  variables: {
                    input: rejectFitupRequestInput,
                  },
                });
              }
            } else
              rejectWeld({
                variables: {
                  input: rejectWeldInput,
                },
              });
          }}
        >
          {({ isValid, dirty, handleSubmit, setFieldValue, values }) => {
            const showSubReasonComment = some(
              values?.subReasonSelectedList,
              (subReasonType) => subReasonType.label == RejectWeldSubReason.OTHER_SUB_REASON
            );
            const subReasons = find(projectMilestoneTypeRejectionReasons, (item) => {
              return item.id === values?.reasonType?.value;
            })?.subReasons;
            const subReasonList =
              subReasons && subReasons.length > 0
                ? subReasons.map(({ id, reason }) => ({
                    label: reason,
                    value: id,
                  }))
                : [];
            const disabled =
              !dirty || !isValid || (subReasonList.length > 0 ? values?.subReasonSelectedList.length === 0 : false);
            return (
              <>
                <SideSheetBody data-testid="side-sheet-body">
                  <SideSheetContentCol
                    style={{ flexDirection: 'column', height: '100%' }}
                    data-testid="side-sheet-control"
                  >
                    <SectionHeader title={t`Select Reason`} />
                    <ReasonList
                      name="reasonType"
                      reasonList={sendBackReasonList}
                      showComment={values?.reasonType?.label === RejectFitupReason.OTHER_REASON}
                      onSelectionChange={(value) => {
                        setFieldValue('subReasonSelectedList', []);
                        setFieldValue('subReasonComment', '');
                        if (
                          value.label !== RejectFitupReason.OTHER_REASON &&
                          values?.reasonComment &&
                          values?.reasonComment.length > 0
                        )
                          setFieldValue('reasonComment', '');
                      }}
                    />
                    {subReasonList && subReasonList.length > 0 && (
                      <>
                        <SectionHeader title={t`Select Sub Reason`} />
                        <SubReasonList
                          name="subReasonSelectedList"
                          subReasonList={subReasonList}
                          showComment={showSubReasonComment}
                          onSelectionChange={(subReasonValue) => {
                            !subReasonValue.some((item) => item.label === RejectWeldSubReason.OTHER_SUB_REASON) &&
                              values?.subReasonComment &&
                              values?.subReasonComment.length > 0 &&
                              setFieldValue('subReasonComment', '');
                          }}
                        />
                      </>
                    )}
                    <SectionHeader
                      title={`${t`Photo(s)`} (${values?.mediaMetadata?.length ? values.mediaMetadata.length : 0}/3)`}
                    />
                    <MediaUpload fieldName="mediaMetadata" />
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled
                        data-testid="weld-cancel"
                        onClick={onClose}
                        disabled={mutationLoading || rejectFitupLoading || rejectFitupRequestLoading}
                      >
                        <Trans> Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <Box>
                      <FooterButtonStyled
                        data-testid="weld-send-back-submit"
                        onClick={() => {
                          handleSubmit();
                        }}
                        disabled={mutationLoading || rejectFitupLoading || rejectFitupRequestLoading || disabled}
                      >
                        {rejectFitupLoading || rejectFitupRequestLoading || mutationLoading ? (
                          <Spinner />
                        ) : (
                          <Trans>Confirm</Trans>
                        )}
                      </FooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </>
            );
          }}
        </Formik>
      )}
      {rejectWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <RejectWeldErrorModal error={rejectWeldError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
